import type { Types as AmplitudeBrowserTypes } from "@amplitude/analytics-browser"
import type { Types as AmplitudeReactNativeTypes } from "@amplitude/analytics-react-native"
import type { AnalyticsBrowser } from "@segment/analytics-next"
import type { SegmentClient as RNSegmentClient } from "@segment/analytics-react-native/lib/typescript/src/analytics"

import type { AnalyticsEvent, AnalyticsIdentifyEvent, QueuedMethodOptions, AnalyticsClient } from "../types"
import { AnalyticsEventTypes } from "../types"

type Client = AnalyticsClient<
  AnalyticsBrowser | RNSegmentClient | AmplitudeBrowserTypes.BrowserClient | AmplitudeReactNativeTypes.ReactNativeClient
>

export const clearEventsQueue = async (client: Client) => {
  if (!client.queuedEvents.length) return

  // Put the first screen/page event at the front of the queue
  const firstPageOrScreenEventIndex = client.queuedEvents.findIndex(
    e => e.type === AnalyticsEventTypes.PAGE || e.type === AnalyticsEventTypes.SCREEN,
  )
  const firstPageOrScreenEvent = client.queuedEvents[firstPageOrScreenEventIndex]
  if (firstPageOrScreenEvent) {
    // 1. Remove the first screen/page events from the queue
    client.queuedEvents.splice(firstPageOrScreenEventIndex, 1)

    // 2. Place the first screen/page events at the front
    client.queuedEvents.unshift(firstPageOrScreenEvent)
  }

  // Merge identify events and put it at the front of the queue
  const identifyEvents = client.queuedEvents.filter(e => e.type === AnalyticsEventTypes.IDENTIFY)
  if (identifyEvents.length > 1) {
    const mergedIdentifyEvent: AnalyticsIdentifyEvent = {
      type: AnalyticsEventTypes.IDENTIFY,
      properties: undefined,
    }

    // Merge userId and traits, reverse to keep the latest traits
    identifyEvents.reverse().forEach(event => {
      const { userId, traits } = event.properties || {}
      if (!mergedIdentifyEvent.properties) {
        mergedIdentifyEvent.properties = { userId: undefined, traits: {} }
      }

      if (userId) {
        mergedIdentifyEvent.properties.userId = userId
      }

      mergedIdentifyEvent.properties.traits = {
        ...mergedIdentifyEvent.properties.traits,
        ...traits,
      }
    })

    // 2. Remove ALL IDENTIFY events from the queue
    client.queuedEvents = client.queuedEvents.filter(e => e.type !== AnalyticsEventTypes.IDENTIFY)

    // 3. Place the merged one at the front
    client.queuedEvents.unshift(mergedIdentifyEvent)
  }

  // Process events in the queue, use "while" for process incoming events
  while (client.queuedEvents.length > 0) {
    // TODO: Create an user-agent/platform universal package
    // eslint-disable-next-line deprecation/deprecation
    const isWeb = typeof navigator !== "undefined" && navigator.product !== "ReactNative"
    const event = client.queuedEvents.shift()
    if (!event) {
      break
    }

    // Identify
    if (event.type === AnalyticsEventTypes.IDENTIFY) {
      await client.identify(event.properties?.userId, event.properties?.traits, { skipQueue: true })
      continue
    }

    // Screen
    if (event.type === AnalyticsEventTypes.SCREEN) {
      if (!event.name || isWeb) continue
      void client.screen(event.name, { skipQueue: true })
      continue
    }

    // Page
    if (event.type === AnalyticsEventTypes.PAGE && event.category) {
      if (!event.name || !isWeb) continue
      const { category, properties } = event

      client.queuedEvents = client.queuedEvents.filter(e => e !== event)
      void client.page(category, event.name, properties, { skipQueue: true })
      continue
    }

    // Track
    if (!event.name) continue
    void client.track(event.name, event.properties, { skipQueue: true })
  }

  // Clear the queue
  client.queuedEvents = []
}

export const pushEventToQueue = (client: Client, event: AnalyticsEvent, pushToStart?: boolean) => {
  if (pushToStart) {
    client.queuedEvents.unshift(event)
    return
  }

  client.queuedEvents.push(event)
}

export const hasEventInQueue = (client: Client, eventType: AnalyticsEventTypes) =>
  client.queuedEvents.filter(e => e.type === eventType).length > 0

/**
 * Determine if the event incoming event when the client is ready should be added to the queue
 * Return true if the client is ready, the queue is processing, and the queue should not be skipped
 */
export const shouldAddEventToTheQueue = (client: Client | undefined, options: QueuedMethodOptions | undefined) => {
  const isProcessingQueue = !!client?.client && !!client.queuedEvents.length
  const skipTheQueue = !!options?.skipQueue && isProcessingQueue

  return isProcessingQueue && !skipTheQueue
}
